import React, { useState, useEffect } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import moment from "moment";
import HomeService from "../Services/HomeService";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

function AddPrimeLocationInsights() {
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedLocalities, setSelectedLocalities] = useState([]);
  const [selectedApartments, setSelectedApartments] = useState([]);
  const [commercials, setCommercials] = useState([""]); // Array of commercial names
  const [url, setUrl] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingInsights, setExistingInsights] = useState([]);
  const [loadingLocalities, setLoadingLocalities] = useState(false);
  const [loadingApartments, setLoadingApartments] = useState(false);

  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Fetch cities and existing insights on component mount
  useEffect(() => {
    fetchCities();
    fetchExistingInsights();
  }, []);

  // Fetch localities when city is selected
  useEffect(() => {
    if (selectedCity) {
      fetchLocalitiesByCity(selectedCity);
    } else {
      setLocalities([]);
      setSelectedLocalities([]);
      setApartments([]);
      setSelectedApartments([]);
    }
  }, [selectedCity]);

  // Fetch apartments when city is selected
  useEffect(() => {
    if (selectedCity) {
      fetchApartmentsByCity(selectedCity);
    } else {
      setApartments([]);
      setSelectedApartments([]);
    }
  }, [selectedCity]);

  // Fetch all cities
  const fetchCities = async () => {
    try {
      const res = await HomeService.getCities();
      if (res?.data) {
        setCities(res.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Failed to fetch cities");
    }
  };

  // Fetch existing prime location insights
  const fetchExistingInsights = async () => {
    try {
      const res = await HomeService.getPrimeLocationInsights();
      if (res?.data) {
        setExistingInsights(res.data);
      }
    } catch (error) {
      console.error("Error fetching existing insights:", error);
    }
  };

  // Fetch localities for selected city
  const fetchLocalitiesByCity = async (cityId) => {
    setLoadingLocalities(true);
    try {
      const response = await HomeService.getLocalities();
      console.log("localities", response);
      if (response?.data) {
        // Filter localities by selected city
        const filteredLocalities = response.data.filter(
          (locality) => locality?.city?._id === cityId,
        );

        // Format localities for react-select
        const formattedLocalities = filteredLocalities.map((locality) => ({
          value: locality._id,
          label: `${locality.name} (${locality.city?.name})`,
          localityData: locality,
        }));

        setLocalities(formattedLocalities);

        if (filteredLocalities.length === 0) {
          toast.info("No localities found for this city");
        }
      }
    } catch (error) {
      console.error("Error fetching localities:", error);
      toast.error("Failed to fetch localities");
    } finally {
      setLoadingLocalities(false);
    }
  };

  // Fetch apartments for selected city
  const fetchApartmentsByCity = async (cityId) => {
    setLoadingApartments(true);
    try {
      const response = await HomeService.getApartment();
      if (response?.data) {
        // Filter apartments by selected city
        const filteredApartments = response.data.filter(
          (apartment) => apartment?.city?._id === cityId,
        );

        // Format apartments for react-select
        const formattedApartments = filteredApartments.map((apartment) => ({
          value: apartment._id,
          label: `${apartment.aptName} - ${apartment.locality?.name || "N/A"}`,
          apartmentData: apartment,
        }));

        setApartments(formattedApartments);

        if (filteredApartments.length === 0) {
          toast.info("No apartments found for this city");
        }
      }
    } catch (error) {
      console.error("Error fetching apartments:", error);
      toast.error("Failed to fetch apartments");
    } finally {
      setLoadingApartments(false);
    }
  };

  // Handle locality selection
  const handleLocalityChange = (selectedOptions) => {
    setSelectedLocalities(selectedOptions || []);
  };

  // Handle apartment selection
  const handleApartmentChange = (selectedOptions) => {
    setSelectedApartments(selectedOptions || []);
  };

  // Handle commercial input change
  const handleCommercialChange = (index, value) => {
    const updatedCommercials = [...commercials];
    updatedCommercials[index] = value;
    setCommercials(updatedCommercials);
  };

  // Add new commercial field
  const addCommercialField = () => {
    if (commercials.length < 10) {
      setCommercials([...commercials, ""]);
    } else {
      toast.info("Maximum 10 commercial spaces allowed");
    }
  };

  // Remove commercial field
  const removeCommercialField = (index) => {
    if (commercials.length > 1) {
      const updatedCommercials = commercials.filter((_, i) => i !== index);
      setCommercials(updatedCommercials);
    }
  };

  // Handle URL change
  const handleUrlChange = (e) => {
    setUrl(e.target.value);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!selectedCity) {
      toast.error("Please select a city");
      return;
    }

    if (selectedLocalities.length === 0) {
      toast.error("Please select at least one locality");
      return;
    }

    if (selectedApartments.length === 0) {
      toast.error("Please select at least one apartment");
      return;
    }

    // Filter out empty commercial entries
    const filteredCommercials = commercials.filter((com) => com.trim() !== "");

    if (filteredCommercials.length === 0) {
      toast.error("Please add at least one commercial space");
      return;
    }

    if (!url) {
      toast.error("Please enter a URL");
      return;
    }

    // URL validation
    try {
      new URL(url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    // Check if insights already exist for this city
    const existingInsight = existingInsights.find(
      (insight) => insight.city === selectedCity,
    );

    if (existingInsight) {
      toast.error(
        `Prime location insights already exist for this city. Please edit the existing one.`,
      );
      return;
    }

    // if (selectedProperties?.length === 0) {
    //   toast.error("Please select Properties");
    //   return;
    // }

    const propertyIds = selectedProperties?.map((item) => {
      return item?.value;
    });

    // Prepare data for API
    const data = {
      city: selectedCity,
      localities: selectedLocalities.map((locality) => locality.value),
      apartments: selectedApartments.map((apartment) => apartment.value),
      commercials: filteredCommercials,
      url: url,
      propertyIds: propertyIds,
    };

    setIsSubmitting(true);
    try {
      const res = await HomeService.createPrimeLocationInsights(data);
      if (res?.status) {
        toast.success("Prime location insights created successfully!");
        // Reset form
        resetForm();
        fetchExistingInsights(); // Refresh the list
      } else {
        toast.error(res?.message || "Failed to create insights");
      }
    } catch (error) {
      console.error("Error creating prime location insights:", error);
      toast.error(
        error?.response?.data?.message || "Failed to create insights",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format city options for dropdown
  const cityOptions = cities.map((city) => ({
    value: city._id,
    label: city.name,
  }));

  // Get selected city name for display
  const selectedCityName = cities.find(
    (city) => city._id === selectedCity,
  )?.name;

  // Get selected locality details for preview
  const selectedLocalityDetails = selectedLocalities.map((locality) => {
    const loc = localities.find((l) => l.value === locality.value);
    return loc ? loc.localityData : null;
  });

  // Get selected apartment details for preview
  const selectedApartmentDetails = selectedApartments.map((apartment) => {
    const apt = apartments.find((a) => a.value === apartment.value);
    return apt ? apt.apartmentData : null;
  });

  //fetch 3000 properties when select city

  const fetchPropertiesWithCityId = async (cityId) => {
    try {
      setLoadingProperties(true);
      const res = await HomeService?.filterProperty({ cityId }, 1, 5000);
      console.log("property", res);
      if (res?.status) {
        const filterCity = res?.data?.filter((item) => {
          return item?.aptName !== "";
        });
        // console.log("filter", filterCity);
        const modifiedProperty = filterCity?.map((item) => {
          return {
            label: `Appartment : ${item?.aptName} , Address : ${item?.address}`,
            value: item?._id,
          };
        });
        setProperties(modifiedProperty);
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      console.log(error);
      toast.error(error);
    } finally {
      setLoadingProperties(false);
    }
  };

  // Handle city change
  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    setSelectedLocalities([]);
    setSelectedApartments([]);
    setCommercials([""]);
    setUrl("");
    setSelectedProperties([]);
    fetchPropertiesWithCityId(e?.target?.value);
  };

  // Get existing insight for selected city
  const existingInsightForSelectedCity = selectedCity
    ? existingInsights.find((insight) => insight.city === selectedCity)
    : null;

  // Reset form
  const resetForm = () => {
    setSelectedCity("");
    setSelectedLocalities([]);
    setSelectedApartments([]);
    setCommercials([""]);
    setSelectedProperties([]);
    setUrl("");
  };

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        <div className="mt-5">
          <h2 className="text-center" style={headLineStyle}>
            Add Prime Location Insights
          </h2>
          <hr />

          <section className="chartBox_area">
            <div className="row">
              <div className="col-lg-12">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    {/* City Selection */}
                    <div className="col-md-6 mt-3">
                      <label>
                        Select City<span className="text-danger">*</span>
                      </label>
                      <select
                        className="form-control"
                        value={selectedCity}
                        onChange={handleCityChange}
                        required
                      >
                        <option value="">---Select City---</option>
                        {cities.map((city, key) => (
                          <option key={key} value={city._id}>
                            {city.name}
                          </option>
                        ))}
                      </select>
                      {existingInsightForSelectedCity && (
                        <div className="alert alert-warning mt-2 mb-0 p-2">
                          <small>
                            <i className="fas fa-exclamation-triangle mr-2"></i>
                            Prime location insights already exist for this city
                            ({selectedCityName})
                          </small>
                        </div>
                      )}
                    </div>

                    {/* URL Input */}
                    <div className="col-md-6 mt-3">
                      <label>
                        Location URL<span className="text-danger">*</span>
                      </label>
                      <input
                        type="url"
                        className="form-control"
                        value={url}
                        onChange={handleUrlChange}
                        placeholder="https://example.com/prime-locations"
                        required
                      />
                      <small className="text-muted">
                        Enter the URL for prime location insights
                      </small>
                    </div>

                    {/* Localities Selection */}
                    {selectedCity && (
                      <div className="col-md-6 mt-3">
                        <label>
                          Select Localities
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          options={localities}
                          value={selectedLocalities}
                          onChange={handleLocalityChange}
                          placeholder={
                            loadingLocalities
                              ? "Loading localities..."
                              : "Search and select localities..."
                          }
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          isLoading={loadingLocalities}
                          noOptionsMessage={() =>
                            loadingLocalities
                              ? "Loading..."
                              : "No localities found"
                          }
                        />
                        <small className="text-muted">
                          Selected: {selectedLocalities.length} localities
                        </small>
                      </div>
                    )}

                    {/* Apartments Selection */}
                    {selectedCity && (
                      <div className="col-md-6 mt-3">
                        <label>
                          Select Apartments
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          options={apartments}
                          value={selectedApartments}
                          onChange={handleApartmentChange}
                          placeholder={
                            loadingApartments
                              ? "Loading apartments..."
                              : "Search and select apartments..."
                          }
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          isLoading={loadingApartments}
                          noOptionsMessage={() =>
                            loadingApartments
                              ? "Loading..."
                              : "No apartments found"
                          }
                        />
                        <small className="text-muted">
                          Selected: {selectedApartments.length} apartments
                        </small>
                      </div>
                    )}

                    {/* Commercial Spaces */}
                    <div className="col-md-12 mt-3">
                      <label>
                        Commercial Spaces<span className="text-danger">*</span>
                      </label>
                      <div className="card">
                        <div className="card-body">
                          {commercials.map((commercial, index) => (
                            <div key={index} className="input-group mb-2">
                              <input
                                type="text"
                                className="form-control"
                                value={commercial}
                                onChange={(e) =>
                                  handleCommercialChange(index, e.target.value)
                                }
                                placeholder={`Commercial Space ${
                                  index + 1
                                } (e.g., Office Tower, Retail Complex)`}
                              />
                              <div className="input-group-append">
                                {commercials.length > 1 && (
                                  <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => removeCommercialField(index)}
                                    title="Remove"
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-2"
                            onClick={addCommercialField}
                          >
                            <i className="fas fa-plus mr-2"></i>
                            Add More Commercial Space
                          </button>
                          {/* <small className="text-muted d-block mt-2">
                            Add commercial spaces (maximum 10)
                          </small> */}
                        </div>
                      </div>
                    </div>

                    {/* select properties */}
                    {/* {selectedCity && (
                      <div className="col-md-6 mt-3">
                        <label>
                          Select Properties
                          <span className="text-danger">*</span>
                        </label>
                        <Select
                          isMulti
                          options={properties}
                          value={selectedProperties}
                          onChange={setSelectedProperties}
                          placeholder={
                            loadingProperties
                              ? "Loading Properties..."
                              : "Search and select Properties..."
                          }
                          className="react-select-container"
                          classNamePrefix="react-select"
                          isClearable={true}
                          isSearchable={true}
                          isLoading={loadingProperties}
                          noOptionsMessage={() =>
                            loadingProperties
                              ? "Loading..."
                              : "No Properties found"
                          }
                        />
                        <small className="text-muted">
                          Selected: {selectedProperties.length} Properties
                        </small>
                      </div>
                    )} */}

                    {/* Preview Section - Localities */}
                    {/* {selectedLocalities.length > 0 && (
                      <div className="col-md-12 mt-4">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h5 className="mb-0">
                              Selected Localities ({selectedLocalities.length})
                            </h5>
                          </div>
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>City</th>
                                    <th>Created Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedLocalityDetails.map(
                                    (locality, index) => (
                                      <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{locality?.name || "N/A"}</td>
                                        <td>{locality?.city?.name || "N/A"}</td>
                                        <td>
                                          {moment(locality?.createdOn).format(
                                            "DD/MM/YYYY"
                                          )}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )} */}

                    {/* Preview Section - Apartments */}
                    {/* {selectedApartments.length > 0 && (
                      <div className="col-md-12 mt-4">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h5 className="mb-0">
                              Selected Apartments ({selectedApartments.length})
                            </h5>
                          </div>
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th>#</th>
                                    <th>Apartment Name</th>
                                    <th>Locality</th>
                                    <th>City</th>
                                    <th>Properties Count</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedApartmentDetails.map(
                                    (apartment, index) => (
                                      <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{apartment?.aptName || "N/A"}</td>
                                        <td>
                                          {apartment?.locality?.name || "N/A"}
                                        </td>
                                        <td>
                                          {apartment?.city?.name || "N/A"}
                                        </td>
                                        <td>
                                          {apartment?.noOfProperties || 0}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )} */}

                    {/* Commercial Spaces Preview */}
                    {/* {commercials.some((com) => com.trim() !== "") && (
                      <div className="col-md-12 mt-4">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h5 className="mb-0">
                              Commercial Spaces (
                              {
                                commercials.filter((com) => com.trim() !== "")
                                  .length
                              }
                              )
                            </h5>
                          </div>
                          <div className="card-body">
                            <ul className="list-group">
                              {commercials.map(
                                (commercial, index) =>
                                  commercial.trim() !== "" && (
                                    <li
                                      key={index}
                                      className="list-group-item d-flex justify-content-between align-items-center"
                                    >
                                      {commercial}
                                      <span className="badge badge-primary badge-pill">
                                        {index + 1}
                                      </span>
                                    </li>
                                  )
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )} */}

                    {/* Existing Insights Table */}
                    {/* {existingInsights.length > 0 && (
                      <div className="col-md-12 mt-4">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              Existing Prime Location Insights
                            </h6>
                          </div>
                          <div className="card-body p-0">
                            <div className="table-responsive">
                              <table className="table table-bordered table-sm mb-0">
                                <thead className="thead-light">
                                  <tr>
                                    <th>City</th>
                                    <th>Localities</th>
                                    <th>Apartments</th>
                                    <th>Commercials</th>
                                    <th>URL</th>
                                    <th>Created Date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {existingInsights.map((insight, index) => {
                                    const city = cities.find(
                                      (c) => c._id === insight.city
                                    );
                                    return (
                                      <tr key={index}>
                                        <td>{city?.name || "Unknown City"}</td>
                                        <td>
                                          {insight.localities?.length || 0}
                                        </td>
                                        <td>
                                          {insight.apartments?.length || 0}
                                        </td>
                                        <td>
                                          {insight.commercials?.length || 0}
                                        </td>
                                        <td>
                                          <a
                                            href={insight.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-truncate d-inline-block"
                                            style={{ maxWidth: "150px" }}
                                          >
                                            {insight.url}
                                          </a>
                                        </td>
                                        <td>
                                          {moment(insight.createdAt).format(
                                            "DD/MM/YYYY"
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    )} */}

                    {/* Submit Button */}
                    <div className="col-md-12 mt-4">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={
                          isSubmitting ||
                          !selectedCity ||
                          selectedLocalities.length === 0 ||
                          selectedApartments.length === 0 ||
                          !commercials.some((com) => com.trim() !== "") ||
                          !url ||
                          existingInsightForSelectedCity
                        }
                      >
                        {isSubmitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm mr-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Creating...
                          </>
                        ) : (
                          "Create Prime Location Insights"
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary ml-2"
                        onClick={resetForm}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>

      <style jsx>{`
        .react-select-container {
          z-index: 1000;
        }

        .table th {
          background-color: #f8f9fa;
        }

        .list-group-item {
          background-color: #f8f9fa;
          border: 1px solid #dee2e6;
        }

        .badge-pill {
          font-size: 0.75em;
        }

        .input-group .btn-danger {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
      `}</style>
    </div>
  );
}

export default AddPrimeLocationInsights;
